import { Document } from "domain/entities/document";
import { Expense } from "domain/entities/expense";
import { ExpenseInputFilter } from "domain/models/expense-input-filter";
import { translateText } from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useDeleteExpenseMutation, useUpdateExpenseMutation,
  useExpenseByFilter,
} from "presentation/hooks/use-query-expense";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFilterExpenseGridStore } from "presentation/stores/use-filter-expense-grid-store";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import HeaderExpenseGrid from "./HeaderExpenseGrid";
import PdfViewerDialog from "presentation/components/Shared/PdfViewerDialog";
import ImageViewerDialog from "presentation/components/Shared/ImageViewerDialog";
import { ExpenseStatusEnum } from "domain/enum/expenseStatus";
import { Dialog } from "primereact/dialog";
import ApprovalForm from "./ApprovalForm";
import { MessageTypeEnum } from "domain/enum/messageType";
import { Tooltip } from "primereact/tooltip";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import ExpenseTypeDropdown from "./ExpenseTypeDropdown";

interface ExpenseGridProps {
  externalFilters?: {
    employeeId?: string;
    month?: number;
    year?: number;
    sortBy?: string;
    sortOrder?: string;
  };
  hideAddExpense?: boolean;
}

const ExpenseGrid: React.FC<ExpenseGridProps> = ({ externalFilters, hideAddExpense }) => {
  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      accept: acceptCallback,
      acceptLabel: "Sim",
      rejectLabel: "Não",
    });
  };

  const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const toast = useRef<Toast>(null);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedExpense(null);
  };

  const { userEmployee } = useAuthStore();
  const { mutate: deleteExpenseMutation, isSuccess: isDeleteExpenseSuccess, isError: isDeleteExpenseError, isPending: isDeleteExpensePending } = useDeleteExpenseMutation();
  const {
    mutate: updateExpenseMutation,
    isSuccess: isUpdateExpenseSuccess,
    isError: isUpdateExpenseError,
    isPending: isUpdateExpensePending,
  } = useUpdateExpenseMutation();

  const { setTotalRecords } = useFilterExpenseGridStore();


  const [lazyParams, setLazyParams] = useState({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
    filters: {} as Record<string, any>,
  });

  const { filters, setFilters, resetFilters } = useFilterExpenseGridStore();

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]: any) => [
            key,
            value?.value ?? ""
          ])
          .filter(([_, value]) => value !== "")
      ),
    }));
  }, [filters]);

  const employeeIdForQuery =
    externalFilters?.employeeId === "*" ||
      externalFilters?.employeeId === "todos"
      ? undefined
      : externalFilters?.employeeId ?? userEmployee?.id;


  const { data: expenses, isPending: isExpensePending, isError, error } =
    useExpenseByFilter({
      page: lazyParams.page,
      limit: lazyParams.limit,
      sortBy: lazyParams.sortBy,
      sortOrder: lazyParams.sortOrder,
      ...lazyParams.filters,
      ...externalFilters,
      employeeId: employeeIdForQuery,
    } as ExpenseInputFilter);

  useEffect(() => {
    if (expenses?.pagination?.count) {
      setTotalRecords(expenses.pagination.count);
    }
  }, [expenses]);


  useEffect(() => {
    if (isDeleteExpenseSuccess) {
      showToast("success", "Success", "Despesa eliminada com sucesso");
    }
  }, [isDeleteExpenseSuccess]);

  useEffect(() => {
    if (isDeleteExpenseError) {
      showToast("error", "Erro", "Despesa não eliminada");
    }
  }, [isDeleteExpenseError]);

  useEffect(() => {
    if (isError) {
      showToast("error", "Erro", error?.message || "Ocorreu um erro inesperado");
    }
  }, [isError, error]);

  useEffect(() => {
    if (isUpdateExpenseSuccess) {
      showToast("success", "Sucesso", "Despesa marcada como paga.");
    }
  }, [isUpdateExpenseSuccess]);

  useEffect(() => {
    if (isUpdateExpenseError) {
      showToast("error", "Erro", "Não foi possível atualizar a despesa.");
    }
  }, [isUpdateExpenseError]);

  if (isExpensePending || isDeleteExpensePending || isUpdateExpensePending) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner />
      </div>
    );
  }

  const statusBodyTemplate = (rowData: Expense) => {
    return (
      <span className={`customer-badge status-${rowData.status}`}>
        {rowData.status}
      </span>
    );
  };

  const formatDate = (value: Date) => {
    return new Date(value).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-PT", {
      style: "currency",
      currency: "EUR",
    });
  };

  const dateBodyTemplate = (rowData: Expense) => {
    return formatDate(rowData.date);
  };

  const submissionDateBodyTemplate = (rowData: Expense) => {
    if (!rowData.submissionDate) return "-";
    return new Date(rowData.submissionDate).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const valueBodyTemplate = (rowData: Expense) => {
    return formatCurrency(rowData.value);
  };

  const showToast = (severity: "success" | "error" | "info" | "warn", summary: string, detail: string) => {
    toast.current?.show({
      severity: severity,
      summary: summary,
      detail: detail,
      life: 1500,
    });
  }

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    showToast(type, subject, message);
  };

  const removeExpense = async (expenseData: Expense) => {
    if (!expenseData?.id) return;
    deleteExpenseMutation(expenseData.id);
  };

  const payExpense = async (expenseData: Expense) => {
    console.log(expenseData);
    if (!expenseData?.id) return;
    updateExpenseMutation({
      ...expenseData,
      status: ExpenseStatusEnum.PAGO,
    });
  };

  const getDocumentFromContabo = async (data: Document) => {
    if (data) {
      let s3Path: string | undefined = "";
      if (data.fullPath) {
        s3Path = data.fullPath;
      }

      if (s3Path) {
        setS3PathToDisplay(s3Path);
      }
    }
  };

  const showActionOptions = (rowData: Expense) => {
    const possibleDeleteStatus = [ExpenseStatusEnum.PENDENTE.toString()];
    const possiblePaymentStatus = [ExpenseStatusEnum.APROVADO.toString()];
    const isPaid = rowData?.status === ExpenseStatusEnum.PAGO;

    let infoMessage = rowData?.description ? 'Descrição: ' + rowData?.description : "";
    infoMessage += infoMessage ? "\n" : "";
    infoMessage += rowData.rejectionReason ? 'Motivo da Rejeição: ' + rowData?.rejectionReason : "";

    if (!hideAddExpense) {

      return (
        <div className="spacing-icons">
          {(rowData?.description || rowData.rejectionReason) && (
            <i
              className="pi pi-info-circle cursor-pointer"
              data-pr-tooltip={infoMessage}
              data-pr-position="top"
            />
          )}
          {isPaid && rowData.modifiedAt && (
            <i
              className="pi pi-check-circle cursor-pointer"
              style={{ color: "var(--green-500)" }}
              data-pr-tooltip={`Despesa paga em ${new Date(rowData.modifiedAt).toLocaleDateString("pt-PT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })}`}
              data-pr-position="top"
            />
          )}
          {

            <i
              className="pi pi-eye cursor-pointer"
              data-pr-tooltip="Visualizar documento"
              data-pr-position="top"
              onClick={async () => await getDocumentFromContabo(rowData.document)}
            />
          }
          {possibleDeleteStatus.includes(rowData?.status ?? "") && (
            <i
              className="pi pi-trash cursor-pointer"
              data-pr-tooltip="Anular submissão"
              data-pr-position="top"
              onClick={() =>
                confirmAction(
                  "Deseja anular a submissão deste documento?",
                  async () => await removeExpense(rowData)
                )
              }
            />
          )}
        </div>
      );
    }

    return (
      <div className="spacing-icons">
        {(rowData?.description || rowData.rejectionReason) && (
          <i
            className="pi pi-info-circle cursor-pointer"
            data-pr-tooltip={infoMessage}
            data-pr-position="top"
          />
        )}
        {isPaid && rowData.modifiedAt && (
          <i
            className="pi pi-check-circle cursor-pointer"
            style={{ color: "var(--green-500)" }}
            data-pr-tooltip={`Despesa paga em ${new Date(rowData.modifiedAt).toLocaleDateString("pt-PT", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}`}
            data-pr-position="top"
          />
        )}
        {possibleDeleteStatus.includes(rowData?.status ?? "") && (
          <i
            className="pi pi-file-check cursor-pointer"
            style={{ color: "var(--primary-color)" }}
            data-pr-tooltip="Aprovar/Rejeitar despesa"
            data-pr-position="top"
            onClick={() => {
              setSelectedExpense(rowData);
              setShowModal(true);
            }}
          />
        )}
        {!possibleDeleteStatus.includes(rowData?.status ?? "") && (
          <i
            className="pi pi-eye cursor-pointer"
            data-pr-tooltip={`Visualizar despesa (${rowData.status})`}
            data-pr-position="top"
            onClick={() => {
              setSelectedExpense(rowData);
              setShowModal(true);
            }}
          />
        )}
        {
          possiblePaymentStatus.includes(rowData?.status) && (
            <i
              className="pi pi-money-bill cursor-pointer"
              style={{ color: "var(--primary-color)" }}
              data-pr-tooltip="Marcar como paga"
              data-pr-position="top"
              onClick={() =>
                confirmAction("Deseja marcar esta despesa como paga?",
                  async () => await payExpense(rowData))
              }
            />
          )}
      </div>
    );
  };


  return (
    <div>
      <DataTable
        value={expenses?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={expenses?.pagination?.count || 0}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        onSort={(e: DataTableSortEvent) => {
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          });
        }}
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        loading={isExpensePending}
        tableStyle={{ minWidth: "50rem" }}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderExpenseGrid hideAddExpense={hideAddExpense} />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
      >
        <Column
          field="employee.name"
          header={translateText("Employee", "pt")}
          sortable
          filter
          filterField="employeeName"
          showFilterMatchModes={false}
        />
        <Column
          field="submissionDate"
          header="Data de Submissão"
          body={submissionDateBodyTemplate}
          sortable
          // filter
          // filterField="createdAt"
          // showFilterMatchModes={false}
        />
        <Column
          field="date"
          header="Data da Despesa"
          body={dateBodyTemplate}
          sortable
          // filter
          // filterField="date"
          // showFilterMatchModes={false}
        />
        <Column
          field="type"
          header={translateText("Expense Type", "pt")}
          sortable
          filter
          filterField="type"
          showFilterMatchModes={false}
          filterElement={(options) => (
            <ExpenseTypeDropdown options={options} />
          )}
        />
        <Column
          field="value"
          header={translateText("Value", "pt")}
          body={valueBodyTemplate}
          sortable
          filterField="value"
          showFilterMatchModes={false}
        />
        <Column
          field="status"
          header={translateText("Status", "pt")}
          body={statusBodyTemplate}
          sortable
          filter
          showFilterMatchModes={false}
          filterField="status"
        />

        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
      {s3PathToDisplay && (s3PathToDisplay.toLowerCase().endsWith(".pdf") ? (
        <PdfViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      ) : (
        <ImageViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      ))}

      <Dialog
        header={`Documento a aprovar`}
        visible={showModal}
        style={{ width: "80vw", height: "90vh" }}
        onHide={handleCloseModal}
      >
        <ApprovalForm
          expense={selectedExpense}
          onClose={handleCloseModal}
          showMessage={showMessage}
        />
      </Dialog>
      <Tooltip target="[data-pr-tooltip]" />
    </div>
  );
};

export default ExpenseGrid;